/**
 * Call Scripts React Query Hooks
 * 
 * Provides React Query hooks for call script operations with caching,
 * optimistic updates, and automatic refetching.
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { 
  callScriptService, 
  type CreateCallScriptInput, 
  type UpdateCallScriptInput 
} from '../services/CallScriptService';
import type { CallScript } from '../repositories/CallScriptRepository';

type ScriptType = CallScript['script_type'];

/**
 * Query keys for call script queries
 */
export const callScriptKeys = {
  all: ['callScripts'] as const,
  lists: () => [...callScriptKeys.all, 'list'] as const,
  list: (filters?: any) => [...callScriptKeys.lists(), filters] as const,
  details: () => [...callScriptKeys.all, 'detail'] as const,
  detail: (id: string) => [...callScriptKeys.details(), id] as const,
  byType: (type: ScriptType) => [...callScriptKeys.lists(), 'type', type] as const,
  counts: () => [...callScriptKeys.all, 'counts'] as const,
};

/**
 * Hook to fetch all call scripts
 * 
 * @example
 * ```tsx
 * const { data: scripts, isLoading } = useCallScripts(companyId);
 * ```
 */
export function useCallScripts(
  companyId: string,
  activeOnly: boolean = false,
  options?: UseQueryOptions<CallScript[], Error>
) {
  return useQuery<CallScript[], Error>({
    queryKey: callScriptKeys.list({ companyId, activeOnly }),
    queryFn: () => callScriptService.getScripts(companyId, activeOnly),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!companyId,
    ...options,
  });
}

/**
 * Hook to fetch call scripts by type
 * 
 * @example
 * ```tsx
 * const { data: greetings } = useCallScriptsByType(companyId, 'greeting');
 * ```
 */
export function useCallScriptsByType(
  companyId: string,
  type: ScriptType,
  options?: UseQueryOptions<CallScript[], Error>
) {
  return useQuery<CallScript[], Error>({
    queryKey: callScriptKeys.byType(type),
    queryFn: () => callScriptService.getScriptsByType(companyId, type),
    staleTime: 5 * 60 * 1000,
    enabled: !!companyId && !!type,
    ...options,
  });
}

/**
 * Hook to fetch a single call script
 * 
 * @example
 * ```tsx
 * const { data: script } = useCallScript(scriptId);
 * ```
 */
export function useCallScript(
  id: string,
  options?: UseQueryOptions<CallScript | null, Error>
) {
  return useQuery<CallScript | null, Error>({
    queryKey: callScriptKeys.detail(id),
    queryFn: () => callScriptService.getScript(id),
    staleTime: 5 * 60 * 1000,
    enabled: !!id,
    ...options,
  });
}

/**
 * Hook to fetch script count by type
 * 
 * @example
 * ```tsx
 * const { data: count } = useCallScriptCountByType(companyId, 'greeting');
 * ```
 */
export function useCallScriptCountByType(
  companyId: string,
  type: ScriptType,
  options?: UseQueryOptions<number, Error>
) {
  return useQuery<number, Error>({
    queryKey: [...callScriptKeys.counts(), companyId, type] as const,
    queryFn: () => callScriptService.getScriptCountByType(companyId, type),
    staleTime: 5 * 60 * 1000,
    enabled: !!companyId && !!type,
    ...options,
  });
}

/**
 * Hook to create a new call script
 * 
 * @example
 * ```tsx
 * const createScript = useCreateCallScript();
 * 
 * const handleSubmit = async (data) => {
 *   await createScript.mutateAsync(data);
 * };
 * ```
 */
export function useCreateCallScript() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateCallScriptInput) => callScriptService.createScript(input),
    onSuccess: (newScript) => {
      // Add to cache
      queryClient.setQueryData(callScriptKeys.detail(newScript.id), newScript);
      
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: callScriptKeys.lists() });
      queryClient.invalidateQueries({ queryKey: callScriptKeys.counts() });
      
      // Invalidate type-specific list if applicable
      if (newScript.script_type) {
        queryClient.invalidateQueries({ 
          queryKey: callScriptKeys.byType(newScript.script_type) 
        });
      }
    },
  });
}

/**
 * Hook to update a call script
 * 
 * @example
 * ```tsx
 * const updateScript = useUpdateCallScript();
 * 
 * const handleSave = async () => {
 *   await updateScript.mutateAsync({
 *     id: scriptId,
 *     data: updatedData
 *   });
 * };
 * ```
 */
export function useUpdateCallScript() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCallScriptInput }) =>
      callScriptService.updateScript(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: callScriptKeys.detail(id) });
      
      const previousScript = queryClient.getQueryData<CallScript>(callScriptKeys.detail(id));
      
      if (previousScript) {
        queryClient.setQueryData<CallScript>(callScriptKeys.detail(id), {
          ...previousScript,
          ...data,
        });
      }
      
      return { previousScript };
    },
    onError: (err, { id }, context) => {
      if (context?.previousScript) {
        queryClient.setQueryData(callScriptKeys.detail(id), context.previousScript);
      }
    },
    onSuccess: (updatedScript, { id }) => {
      queryClient.setQueryData(callScriptKeys.detail(id), updatedScript);
      queryClient.invalidateQueries({ queryKey: callScriptKeys.lists() });
      
      if (updatedScript.script_type) {
        queryClient.invalidateQueries({ 
          queryKey: callScriptKeys.byType(updatedScript.script_type) 
        });
      }
    },
  });
}

/**
 * Hook to toggle script active status
 * 
 * @example
 * ```tsx
 * const toggleActive = useToggleCallScriptActive();
 * 
 * const handleToggle = async () => {
 *   await toggleActive.mutateAsync(scriptId);
 * };
 * ```
 */
export function useToggleCallScriptActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => callScriptService.toggleActive(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: callScriptKeys.detail(id) });
      
      const previousScript = queryClient.getQueryData<CallScript>(callScriptKeys.detail(id));
      
      if (previousScript) {
        queryClient.setQueryData<CallScript>(callScriptKeys.detail(id), {
          ...previousScript,
          active: !previousScript.active,
        });
      }
      
      return { previousScript };
    },
    onError: (err, id, context) => {
      if (context?.previousScript) {
        queryClient.setQueryData(callScriptKeys.detail(id), context.previousScript);
      }
    },
    onSuccess: (updatedScript, id) => {
      queryClient.setQueryData(callScriptKeys.detail(id), updatedScript);
      queryClient.invalidateQueries({ queryKey: callScriptKeys.lists() });
      
      if (updatedScript.script_type) {
        queryClient.invalidateQueries({ 
          queryKey: callScriptKeys.byType(updatedScript.script_type) 
        });
      }
    },
  });
}

/**
 * Hook to delete a call script
 * 
 * @example
 * ```tsx
 * const deleteScript = useDeleteCallScript();
 * 
 * const handleDelete = async () => {
 *   await deleteScript.mutateAsync(scriptId);
 * };
 * ```
 */
export function useDeleteCallScript() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => callScriptService.deleteScript(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: callScriptKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: callScriptKeys.lists() });
      queryClient.invalidateQueries({ queryKey: callScriptKeys.counts() });
    },
  });
}
